import { useState, useEffect, useCallback } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { formatError } from "../utils/formatError";
import { fromDoc } from "../utils/firestoreDoc";
import logger from "../utils/logger";
import type { RateTier } from "../types";

export function useRateTiers(enabled = true, agencyId: string | null = null) {
  const [tiers, setTiers] = useState<RateTier[]>([]);
  const [fetchKey, setFetchKey] = useState(0);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const depsKey = `${agencyId || ""}:${fetchKey}`;
  const loading = enabled && !!agencyId && loadedFor !== depsKey;

  useEffect(() => {
    if (!enabled || !agencyId) return;
    let cancelled = false;
    setError(null);
    const q = query(
      collection(db, "rateTiers"),
      where("agencyId", "==", agencyId)
    );
    const key = `${agencyId}:${fetchKey}`;
    getDocs(q)
      .then((snapshot) => {
        if (!cancelled) {
          setTiers(snapshot.docs.map((d) => fromDoc<RateTier>(d)));
          setLoadedFor(key);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          logger.error("Failed to load rate tiers:", err);
          setError(formatError(err));
          setLoadedFor(key);
        }
      });
    return () => { cancelled = true; };
  }, [enabled, agencyId, fetchKey]);

  const refetch = useCallback(() => setFetchKey((k) => k + 1), []);

  const createTier = useCallback(async (data: Partial<RateTier> & { agencyId: string; rate: number | string }) => {
    try {
      await addDoc(collection(db, "rateTiers"), {
        ...data,
        rate: Number(data.rate),
        agencyId: data.agencyId,
        createdAt: serverTimestamp(),
      });
      refetch();
    } catch (err) {
      throw new Error(formatError(err));
    }
  }, [refetch]);

  const updateTier = useCallback(async (tierId: string, data: Partial<RateTier> & { rate: number | string }) => {
    try {
      await updateDoc(doc(db, "rateTiers", tierId), {
        ...data,
        rate: Number(data.rate),
        updatedAt: serverTimestamp(),
      });
      refetch();
    } catch (err) {
      throw new Error(formatError(err));
    }
  }, [refetch]);

  const deleteTier = useCallback(async (tierId: string) => {
    try {
      await deleteDoc(doc(db, "rateTiers", tierId));
      refetch();
    } catch (err) {
      throw new Error(formatError(err));
    }
  }, [refetch]);

  return { tiers, loading, error, createTier, updateTier, deleteTier };
}
